// src/components/ChartCard.jsx
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, IconButton, FormControl, Select, MenuItem,
  Stack, InputLabel, Button, Checkbox, ListItemText, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, ToggleButtonGroup, ToggleButton,
  Tooltip as MuiTooltip, Divider, Paper, Menu, ListItemIcon
} from '@mui/material';
import {
  TableChart as TableChartIcon, ShowChart as ShowChartIcon,
  Edit as EditIcon, North as NorthIcon, South as SouthIcon, SwapVert as SwapVertIcon,
  SwapHoriz as SwapHorizIcon, Percent as PercentIcon, LooksOne as LooksOneIcon,
  People as PeopleIcon, FormatListNumbered as FormatListNumberedIcon,
  NotInterested as NotInterestedIcon, PlaylistAdd as PlaylistAddIcon,
  Download as DownloadIcon, PictureAsPdf as PictureAsPdfIcon,
  Image as ImageIcon, CropOriginal as CropOriginalIcon, Slideshow as SlideshowIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  ScatterChart, Scatter, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ResponsiveContainer
} from 'recharts';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import PptxGenJS from 'pptxgenjs';
import { jsPDF } from 'jspdf';
import { BASE_COLOR_THEMES, DEFAULT_THEME_ID, getThemeById } from '../constants/colorThemes.js';

const DEFAULT_SETTINGS = {
  legend: true,
  dataLabels: true,
  gridLines: true,
  autoScale: true, // false = start Y‑axis at zero
  visibleAnswers: null, // null → tüm cevaplar açık
  baseType: 'participants',          // 'none' | 'participants' | 'answers'
};




const sortData = (data, key, order) => {
  if (order === 'none') return data;
  const sorted = [...data].sort((a, b) =>
    order === 'asc' ? a[key] - b[key] : b[key] - a[key]
  );
  return sorted;
};





const roundPercentages = (values, decimals = 1) => {
  const total  = values.reduce((s, v) => s + v, 0) || 1;
  const factor = 10 ** decimals;

  const raw     = values.map(v => (v * 100 * factor) / total);   // ölçekli
  let   rounded = raw.map(v => Math.round(v));                   // ön-yuvarlama
  let   diff    = factor * 100 - rounded.reduce((s, v) => s + v, 0);

  if (diff !== 0) {
    // Pozitif farkı en büyük, negatif farkı en küçük artık değere ekle
    const remainders = raw.map((v, i) => v - rounded[i]);
    const idx = diff > 0
      ? remainders.indexOf(Math.max(...remainders))
      : remainders.indexOf(Math.min(...remainders));
    rounded[idx] += diff;
  }
  return rounded.map(v => v / factor);
};

/** Bir kaydı (%100’e normallemiş) {key:pct,…} nesnesi döndürür. */
const normalizePercentRow = (rec, keys, decimals = 1) => {
  const pcts = roundPercentages(keys.map(k => rec[k] ?? 0), decimals);
  return keys.reduce((o, k, i) => ({ ...o, [k]: pcts[i] }), {});
};

/**
 * Nicely format a numeric value as percent with up to 1 decimal,
 * removing trailing “.0” and rounding floating‑point artefacts (e.g. 100.000000001).
 */
const fmtPercent = (v, decimals = 1) => {
  const n = +v.toFixed(decimals);         // round & convert → trims trailing zeros
  return `${n}%`;
};

/* Veri–grafik uyumluluğu kontrolü */
const isCompatible = (type, d) => {
  switch (type) {
    case 'histogram':
      return d?.histogram?.bins && d.histogram?.counts;
    case 'bar':
    case 'line':
    case 'area':
    case 'pie':
    case 'scatter':
    case 'radar':
      return Array.isArray(d?.labels) && Array.isArray(d?.counts);
    case 'barGroup':
      return Array.isArray(d?.records) && Array.isArray(d?.series);
    case 'crosstabGroup':
      return Array.isArray(d?.records) && Array.isArray(d?.series);
    case 'awareness':
      return Array.isArray(d?.records) && Array.isArray(d?.series);  
    case 'transposeGroup':
      return Array.isArray(d?.records) && Array.isArray(d?.series);
    default:
      return false;
  }
};

/* --------------------------------------------------------------- */
/*  Cevap Seçim Diyaloğu                                           */
/* --------------------------------------------------------------- */
function AnswerSelectDialog({ open, onClose, options = [], selected = [], onSave }) {
  const [local, setLocal] = useState(selected);
  useEffect(() => setLocal(selected), [selected]);

  const toggle = (opt) => {
    setLocal((prev) =>
      prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cevapları Göster / Gizle</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: 400 }}>
        {options.map((opt) => (
          <Stack
            key={opt}
            direction="row"
            alignItems="center"
            sx={{ mb: 0.5 }}
            spacing={1}
          >
            <Checkbox checked={local.includes(opt)} onChange={() => toggle(opt)} />
            <Typography variant="body2">{opt}</Typography>
          </Stack>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Vazgeç</Button>
        <Button
          variant="contained"
          onClick={() => onSave(local.length === options.length ? null : local)}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
}



export default function ChartCard({
  graph,
  onAddToPresentation,
  onSaveSettings,
  // Yeni: context yerine prop geçiriyoruz
  showVarLabels,
  setShowVarLabels,
  // Yeni: başlıkları/serileri formatlamak için fonksiyon
  formatVarLabel,         // örn: (hdr) => "VarLabel (HDR)" / "HDR"
  colorThemes,
  defaultColorThemeId,
  persistedUi,
  onSaveUi,
}) {
  /* ------------------ State & helpers ----------------------- */
  const [answerDlg,   setAnswerDlg]   = useState(false);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS, ...(graph.settings || {}) });
  // Sync when parent updates graph.settings (e.g., Apply‑to‑all)
  useEffect(() => {
    setSettings({ ...DEFAULT_SETTINGS, ...(graph.settings || {}) });
  }, [graph.settings]);

 const fmtVar = useCallback((x) => (formatVarLabel ? formatVarLabel(x) : String(x ?? '')), [formatVarLabel]);

  // ColumnTypes meta lookup (graph -> window -> empty)
  const columnTypes = useMemo(() => {
    return graph?.columnTypes || window.__columnTypes || [];
  }, [graph?.columnTypes]);


  // Meta kaynağı: grafikle gelen → global window → yoksa boş
  const headerMetaMap = useMemo(() => {
    const metaSrc =
      graph?.columnTypes ||           // sunucudan grafikle birlikte geldiyse
      window.__columnTypes ||         // sayfa genelinde set ettiysen
      window.__projectRawData ||      // ProjectsPage’den aktarıyorsan
      [];
    const m = new Map();
    (Array.isArray(metaSrc) ? metaSrc : []).forEach((c) => {
      if (c && c.header) m.set(String(c.header), c);
    });
    return m;
  }, [graph?.columnTypes]);
  const baseFileName = useMemo(() => {
    const raw = graph?.header ?? 'chart';
    const normalized = String(raw)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9-_ ]/g, ' ')
      .trim()
      .replace(/\s+/g, '-');
    return normalized || 'chart';
  }, [graph?.header]);
  
  // --- Determine which chart types should be offered in the Select ---
  const MQ_TYPES = ['barGroup', 'transposeGroup', 'crosstabGroup', 'awareness']; // grid / multigrid / pivot / multiquestion
  const allowedTypes = useMemo(
    () =>
      MQ_TYPES.includes(graph.graphType)
        ? MQ_TYPES                                          // only these for multi‑question data
        : ['histogram', 'bar', 'line', 'area', 'pie', 'scatter', 'radar'], // simple questions
    [graph.graphType]
  );

  const themeList = Array.isArray(colorThemes) && colorThemes.length ? colorThemes : BASE_COLOR_THEMES;
  const fallbackThemeId = themeList[0]?.id || DEFAULT_THEME_ID;

  const [chartType, setChartType]     = useState((persistedUi && persistedUi.chartType) || graph.graphType || 'bar');
  const [sortOrder, setSortOrder]     = useState((persistedUi && persistedUi.sortOrder) || 'none');
  const [metric, setMetric]           = useState((persistedUi && persistedUi.metric) || 'percentage');
  const [orientation, setOrientation] = useState((persistedUi && persistedUi.orientation) || 'horizontal');
  const initialTheme =
    (persistedUi && persistedUi.colorTheme) ||
    defaultColorThemeId ||
    fallbackThemeId;
  const [colorThemeId, setColorThemeId] = useState(initialTheme);
  const colorThemeOverrideRef = useRef(Boolean(persistedUi && persistedUi.colorTheme));
  const [showTable, setShowTable]     = useState(Boolean(persistedUi && persistedUi.showTable));

  const [baseType, setBaseType] = useState(settings.baseType || 'none');

  useEffect(() => {
    const persistedTheme = persistedUi && persistedUi.colorTheme;
    if (typeof persistedTheme === 'string' && persistedTheme.trim()) {
      colorThemeOverrideRef.current = true;
      if (persistedTheme !== colorThemeId) {
        setColorThemeId(persistedTheme);
      }
      return;
    }

    const fallback = defaultColorThemeId || fallbackThemeId;

    if (!colorThemeOverrideRef.current && colorThemeId !== fallback) {
      setColorThemeId(fallback);
      return;
    }

    if (!persistedTheme && colorThemeId === fallback) {
      colorThemeOverrideRef.current = false;
    }
  }, [persistedUi?.colorTheme, defaultColorThemeId, fallbackThemeId, colorThemeId]);

  const activeTheme = getThemeById(colorThemeId, themeList);
  const themePalette = activeTheme.palette || [];
  const primaryColor = themePalette[0] || '#8639A1';
  const palette = themePalette.length ? themePalette : [primaryColor];
  

  // ---- Lazy‑render support -----------------------------------
  const cardRef = useRef(null);
  const chartContentRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();       // stop observing after first reveal
        }
      },
      { threshold: 0.1 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  /* Visible‑answer helpers */
  const allAnswerOptions = useMemo(() => {
    if (chartType === 'barGroup') return graph.data.records.map((r) => r.label);
    if (chartType === 'crosstabGroup')
      return graph.data.records.map((r) => r.answer ?? r.label);
    if (['bar', 'line', 'area', 'pie', 'scatter', 'radar'].includes(chartType))
      return graph.data.labels;
    if (chartType === 'histogram' && graph.data.histogram?.bins) {
      const { bins } = graph.data.histogram;
      return bins.slice(0, -1).map((b, i) =>
        `${b.toFixed(1)}-${bins[i + 1].toFixed(1)}`
      );
    }
    return [];
  }, [chartType, graph.data]);

  const visibleSet = useMemo(() => {
    if (!settings.visibleAnswers || settings.visibleAnswers === null) return null;
    return new Set(settings.visibleAnswers);
  }, [settings.visibleAnswers]);

  /* Helper for record‑level filtering */
  const filterByVisibility = useCallback(
    (records, labelGetter = (r) => r.label) => {
      if (!visibleSet) return records;
      return records.filter((r) => visibleSet.has(labelGetter(r)));
    },
    [visibleSet]
  );

  /* ------ callbacks -------- */
  const handleCloseDownloadMenu = useCallback(() => {
    setDownloadAnchorEl(null);
  }, []);

  const handleOpenDownloadMenu = useCallback((event) => {
    if (isExporting) return;
    setDownloadAnchorEl(event.currentTarget);
  }, [isExporting]);


  const handleSaveAnswers = (selected) => {
    const newSet = { ...settings, visibleAnswers: selected };
    setSettings(newSet);
    setAnswerDlg(false);
    onSaveSettings(graph.header, newSet);
  };

  /* ---------------- Data processing ------------------------- */
  const processed = useMemo(() => {
    if (!['bar', 'histogram', 'line', 'area', 'pie', 'scatter', 'radar'].includes(chartType)) {
      return [];
    }
    if (!isCompatible(chartType, graph.data)) return [];

    const makeBase = () => {
      switch (chartType) {
        case 'histogram': {
          const { bins, counts } = graph.data.histogram ?? {};
          if (!bins || !counts) return [];
          return counts.map((cnt, i) => ({
            label: `${bins[i].toFixed(1)}-${bins[i + 1].toFixed(1)}`,
            value: cnt,
          }));
        }
        default: {
          return graph.data.labels.map((lbl, i) => ({
            label: lbl,
            value: graph.data.counts[i],
            index: i,
          }));
        }
      }
    };

    let base = makeBase();
    base = filterByVisibility(base, (r) => r.label);
    base = sortData(base, 'value', sortOrder);

    // NEW: Percentage must use Baz (participants/answers) instead of total sum.
    if (metric === 'percentage') {
      const bases = graph?.bases || {};
      // Prefer participants as default; allow 'answers' if user toggled Baz tipi.
      const preferred = (baseType === 'answers')
        ? (bases.answers ?? 0)
        : (bases.participants ?? 0);

      // Fallback to previous behavior if Baz not provided.
      const den = preferred > 0
        ? preferred
        : (base.reduce((s, r) => s + (r.value || 0), 0) || 1);

      base = base.map((r) => ({
        ...r,
        value: +(((r.value || 0) * 100) / den).toFixed(1),
      }));
    }
    return base;
  }, [chartType, graph.data, sortOrder, metric, filterByVisibility, baseType, graph.bases]);

  const buildFileName = useCallback((ext) => `${baseFileName}.${ext}`, [baseFileName]);

  const captureChartAsCanvas = useCallback(async (backgroundColor) => {
    if (!chartContentRef.current) {
      throw new Error('Grafik içeriği henüz hazır değil');
    }
    const element = chartContentRef.current;
    const scale =
      typeof window !== 'undefined' && window.devicePixelRatio
        ? Math.max(2, window.devicePixelRatio)
        : 2;
    const canvas = await html2canvas(element, {
      backgroundColor,
      scale,
      useCORS: true,
      logging: false,
    });
    return canvas;
  }, []);

  const exportAsPptx = useCallback(async () => {
    const pptx = new PptxGenJS();
    pptx.defineLayout({ name: 'CUSTOM_WIDE', width: 13.33, height: 7.5 });
    pptx.layout = 'CUSTOM_WIDE';

    const slide = pptx.addSlide();
    const title = graph?.header ? String(graph.header) : 'Grafik';
    slide.addText(title, {
      x: 0.5,
      y: 0.4,
      w: 12.3,
      h: 0.6,
      fontSize: 22,
      bold: true,
      color: '2F3C4C',
    });

    const supportedEditable = ['bar', 'histogram', 'line', 'area', 'pie', 'scatter'];
    const canUseEditable =
      !showTable &&
      supportedEditable.includes(chartType) &&
      (processed?.length ?? 0) > 0;

    if (canUseEditable) {
      const labels = processed.map((item) => String(item.label));
      const values = processed.map((item) => Number(item.value) || 0);
      const seriesLabel = metric === 'percentage' ? 'Yüzde' : 'Değer';
      let chartTypeId = 'bar';
      const chartData = [{
        name: seriesLabel,
        labels,
        values,
      }];

      const chartOptions = {
        x: 0.7,
        y: 1,
        w: 11.5,
        h: 5.6,
        showLegend: chartType === 'pie' ? true : settings.legend,
        barDir: orientation === 'horizontal' ? 'bar' : 'col',
        dataLabelPosition: settings.dataLabels ? 'bestFit' : 'none',
        dataLabelFormatCode: metric === 'percentage' ? '0.0%' : undefined,
        catAxisLabelColor: '4A5568',
        valAxisLabelColor: '4A5568',
        valAxisTitle: metric === 'percentage' ? 'Yüzde' : 'Değer',
      };

      switch (chartType) {
        case 'line':
          chartTypeId = 'line';
          break;
        case 'area':
          chartTypeId = 'area';
          break;
        case 'pie':
          chartTypeId = 'pie';
          delete chartOptions.barDir;
          chartOptions.showLegend = true;
          chartOptions.dataLabelPosition = settings.dataLabels ? 'bestFit' : 'none';
          break;
        case 'scatter':
          chartTypeId = 'scatter';
          chartData[0].values = processed.map((item, idx) => ({
            x: typeof item.index === 'number' ? item.index : idx + 1,
            y: Number(item.value) || 0,
          }));
          chartOptions.dataLabelPosition = 'none';
          break;
        default:
          chartTypeId = 'bar';
      }

      slide.addChart(chartTypeId, chartData, chartOptions);
    } else {
      const canvas = await captureChartAsCanvas('#ffffff');
      const dataUrl = canvas.toDataURL('image/png', 1);
      const maxWidth = 11.5;
      const maxHeight = 5.6;
      let imgWidth = maxWidth;
      let imgHeight = (canvas.height / canvas.width) * imgWidth;
      if (imgHeight > maxHeight) {
        const scale = maxHeight / imgHeight;
        imgHeight = maxHeight;
        imgWidth *= scale;
      }
      slide.addImage({
        data: dataUrl,
        x: 0.7 + (maxWidth - imgWidth) / 2,
        y: 1 + (maxHeight - imgHeight) / 2,
        w: imgWidth,
        h: imgHeight,
      });
    }

    await pptx.writeFile({ fileName: buildFileName('pptx') });
  }, [
    buildFileName,
    captureChartAsCanvas,
    chartType,
    graph?.header,
    metric,
    orientation,
    processed,
    settings.dataLabels,
    settings.legend,
    showTable,
  ]);

  const handleDownloadRequest = useCallback(async (format) => {
    handleCloseDownloadMenu();
    if (!chartContentRef.current) return;

    setIsExporting(true);
    try {
      if (format === 'pptx') {
        await exportAsPptx();
        return;
      }

      const canvas = await captureChartAsCanvas(format === 'png' ? null : '#ffffff');

      if (format === 'png' || format === 'jpeg') {
        const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
        const dataUrl = canvas.toDataURL(mimeType, 1);
        const blob = await (await fetch(dataUrl)).blob();
        saveAs(blob, buildFileName(format));
        return;
      }

      if (format === 'pdf') {
        const dataUrl = canvas.toDataURL('image/jpeg', 1);
        const orientationPdf = canvas.width >= canvas.height ? 'l' : 'p';
        const pdf = new jsPDF({
          orientation: orientationPdf,
          unit: 'px',
          format: [canvas.width, canvas.height],
        });
        pdf.addImage(dataUrl, 'JPEG', 0, 0, canvas.width, canvas.height);
        pdf.save(buildFileName('pdf'));
      }
    } catch (err) {
      console.error('Grafik dışa aktarılırken hata oluştu', err);
    } finally {
      setIsExporting(false);
    }
  }, [
    buildFileName,
    captureChartAsCanvas,
    exportAsPptx,
    handleCloseDownloadMenu,
  ]);

  /* --- barGroup & crosstab helpers with visibility ---------- */
  const filteredRecords = useMemo(() => {
    if (chartType === 'barGroup' || chartType === 'crosstabGroup') {
      const labGetter = (r) => r.label ?? r.answer;
      return filterByVisibility(graph.data.records, labGetter);
    }
    return graph.data.records;
  }, [chartType, graph.data.records, filterByVisibility]);

  const hasPivot = Boolean(graph.data.records?.[0]?.pivot);
  const hasRowPivot = chartType === 'barGroup' && (graph.data.rowFields?.length ?? 0) > 1;
  const [groupBy, setGroupBy] = useState((persistedUi && persistedUi.groupBy) || (hasPivot ? 'pivot' : 'answer'));
  const [rowGroupBy, setRowGroupBy] = useState((persistedUi && persistedUi.rowGroupBy) || (hasRowPivot ? graph.data.rowFields.slice(0, 1) : []));

  // Sync local UI state with persistedUi if it changes
  useEffect(() => {
    if (!persistedUi) return;
    if (persistedUi.chartType && persistedUi.chartType !== chartType) setChartType(persistedUi.chartType);
    if (typeof persistedUi.sortOrder !== 'undefined' && persistedUi.sortOrder !== sortOrder) setSortOrder(persistedUi.sortOrder);
    if (typeof persistedUi.metric !== 'undefined' && persistedUi.metric !== metric) setMetric(persistedUi.metric);
    if (typeof persistedUi.orientation !== 'undefined' && persistedUi.orientation !== orientation) setOrientation(persistedUi.orientation);
    if (typeof persistedUi.colorTheme !== 'undefined' && persistedUi.colorTheme !== colorThemeId) setColorThemeId(persistedUi.colorTheme);
    if (typeof persistedUi.showTable !== 'undefined' && Boolean(persistedUi.showTable) !== showTable) setShowTable(Boolean(persistedUi.showTable));
    if (typeof persistedUi.groupBy !== 'undefined' && persistedUi.groupBy !== groupBy) setGroupBy(persistedUi.groupBy);
    if (Array.isArray(persistedUi.rowGroupBy)) {
      const same = JSON.stringify(persistedUi.rowGroupBy) === JSON.stringify(rowGroupBy);
      if (!same) setRowGroupBy(persistedUi.rowGroupBy);
    }
  }, [persistedUi]);

  /* ----- crosstab helpers (respect visibility) -------------- */
  const groupedTotals = useMemo(() => {
    if (chartType !== 'crosstabGroup') return [];
    const serKeys = graph.data.series;
    const mainField = groupBy;
    const otherField = mainField === 'pivot' ? 'answer' : 'pivot';
    const map = {};

    filteredRecords.forEach((rec) => {
      const key = rec[mainField];
      if (!map[key]) {
        map[key] = {
          ...serKeys.reduce((o, k) => ({ ...o, [k]: 0 }), {}),
          [mainField]: key,
          [otherField]: '',
          __isTotal: true,
        };
      }
      serKeys.forEach((k) => {
        map[key][k] += rec[k];
      });
    });
    return Object.values(map);
  }, [chartType, groupBy, filteredRecords, graph.data.series]);

  const groupedWithDetails = useMemo(() => {
    if (chartType !== 'crosstabGroup') return filteredRecords;
    const mainField = groupBy;
    const map = new Map();
    filteredRecords.forEach((r) => {
      const key = r[mainField];
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    const out = [];
    const serKeys = graph.data.series;
    map.forEach((rows, key) => {
      const total = serKeys.reduce(
        (o, k) => ({ ...o, [k]: rows.reduce((s, r) => s + r[k], 0) }),
        { [mainField]: key, __isTotal: true }
      );
      out.push(total);
      out.push(...rows.map((r) => ({ ...r, __isChild: true })));
    });
    return out;
  }, [chartType, groupBy, filteredRecords, graph.data.series]);

  /* --- barGroup hierarchy (respect visibility) -------------- */
  const buildHierarchy = (recs, level = 0) => {
    if (level >= rowGroupBy.length) return recs.map((r) => ({ ...r, __level: level }));
    const col = rowGroupBy[level];
    const groups = {};
    recs.forEach((r) => {
      const key = r[col];
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    });
    const serKeys = graph.data.series;
    const out = [];
    Object.entries(groups).forEach(([val, rows]) => {
      const total = serKeys.reduce(
        (o, k) => ({ ...o, [k]: rows.reduce((s, r) => s + r[k], 0) }),
        { [col]: val, __isTotal: true, __level: level }
      );
      out.push(total);
      out.push(...buildHierarchy(rows, level + 1));
    });
    return out;
  };

  const hierarchicalRows = useMemo(() => {
    if (chartType !== 'barGroup' || !hasRowPivot) return filteredRecords;
    return buildHierarchy(filteredRecords);
  }, [chartType, hasRowPivot, rowGroupBy, filteredRecords]);

  
  /* ----------------------------------------------------------- */
  /*  DataGrid Builders (uses filteredRecords)                   */
  /* ----------------------------------------------------------- */
  const buildGridMeta = () => {
    /* barGroup */
    if (chartType === 'barGroup') {
      const serKeys = graph.data.series;
      const source = hasRowPivot ? hierarchicalRows : filteredRecords;;
      const TOTAL_COL = '__total';

      const columns = [
        ...graph.data.rowFields.map((f) => ({ field: f, headerName: f, width: 160 })),
        ...serKeys.map((k) => ({
          field: k,
          headerName: metric === 'percentage' ? `${formatVarLabel(k)} %` : formatVarLabel(k),         
          width: 120,
          type: 'number',
          renderCell: (params) => `${params.value}`,
        })),
        {
          field: TOTAL_COL,
          headerName: metric === 'percentage' ? 'Toplam %' : 'Toplam',
          width: 140,
          type: 'number',
        },
      ];

      const rows = source.map((r, i) => {
        const participantsDen = graph?.bases?.participants ?? 0;
        const baseTotal = (graph.data.series || []).reduce((s,kk)=> s + (r[kk] ?? 0), 0) || 1;
        const den = participantsDen > 0 ? participantsDen : baseTotal;
        const vals = serKeys.reduce((o, k) => {
          const raw = r[k] ?? 0;
          const val = metric === 'percentage' ? +((raw * 100) / den).toFixed(1) : raw;
          return { ...o, [k]: val };
        }, {});
        const total = (graph.data.series || []).reduce((s, k) => s + (r[k] ?? 0), 0) || 1;
        return {
          id: i,
          ...r,
          ...vals,
          [TOTAL_COL]: metric === 'percentage' ? 100 : total,
        };
      });

      return { rows, columns };
    }

    /* crosstabGroup */
    if (chartType === 'crosstabGroup') {
      const serKeys = graph.data.series;
      const baseRows = hasPivot ? groupedWithDetails : filteredRecords;
      const columns = (hasPivot
        ? [
            { field: 'pivot', headerName: 'Pivot', width: 200 },
            { field: 'answer', headerName: 'Cevap', width: 200 },
          ]
        : [{ field: 'answer', headerName: 'Cevap', width: 240 }]
      ).concat(
        ...serKeys.map((k) => ({
          field: k,
          headerName: k === 'Mean'
            ? 'Mean'
            : (metric === 'percentage' ? `${formatVarLabel(k)} %` : formatVarLabel(k)),
          width: 120,
          type: 'number',
          renderCell: (params) => {
            if (k === 'Mean') return String(params.value ?? '');
            const v = params.value;
            return `${v}`;
          },
        })),
      );

      const rows = baseRows.map((r, i) => {
        const participantsDen = graph?.bases?.participants ?? 0;
        const baseTotal = (graph.data.series || []).reduce((s,k)=> s + (r[k] ?? 0), 0) || 1;
        const den = participantsDen > 0 ? participantsDen : baseTotal;
        const vals = serKeys.reduce((o,k)=> {
          const raw = r[k] ?? 0;
          const val = metric === 'percentage' ? +((raw * 100) / den).toFixed(1) : raw;
          return { ...o, [k]: val };
        }, {});
        return { id:i, answer: r.answer ?? r.label, ...r, ...vals };
      });

      return { rows, columns };
    }

    /* awareness */
    if (chartType === 'awareness') {
      const serKeys = graph.data.series;
      const columns = [
        { field: 'label', headerName: 'Marka', width: 220 },
        ...serKeys.map(k => ({
          field: k,
          headerName: metric === 'percentage' ? `${formatVarLabel(k)} %` : formatVarLabel(k),
          width: 120,
          type: 'number',
        })),
      ];
      const rows = graph.data.records.map((r, i) => {
        const base = {};
        serKeys.forEach(k => {
          base[k] = r[k];
        });
        return { id:i, label:r.label, ...base };
      });
      return { rows, columns };
    }

    /* basit tablolar */
    const columns = [
      { field: 'label', headerName: 'Etiket', width: 220 },
      {
        field: 'value',
        headerName: metric === 'percentage' ? 'Değer (%)' : 'Değer',
        width: 120,
        type: 'number',
      },
    ];
    let rows = (Array.isArray(processed) ? processed : []).map((r, i) => ({ ...r, id: i }));

    return { rows, columns };
  };



  function buildTransposedData(graphData) {
    // orijinal barGroup verisi
    const { records, series } = graphData;      // series = ["M7_c1", …]
    // Y-ekseni artık M7_c* listesi olacak
    return series.map((ser) => {
      const row = { label: ser };               // kategoriler
      records.forEach((rec) => {
        row[rec.label] = rec[ser];              // her cevabı ayrı bar’a dönüştür
      });
      return row;
    });
  }

  /* ----------------------------------------------------------- */
  /*  Grafik Render (uses filtered/processed)                    */
  /* ----------------------------------------------------------- */
  const renderChart = () => {
    if (!isCompatible(chartType, graph.data)) {
      return (
        <Typography color="warning.main" variant="body2">
          Seçilen grafik türü bu veriyle desteklenmiyor
        </Typography>
      );
    }
    const isPercent = metric === 'percentage';
    const domainMax = isPercent ? 100 : 'dataMax';
    const valueFmt = (v) => (isPercent ? fmtPercent(v) : v);
    const grid = settings.gridLines ? <CartesianGrid strokeDasharray="3 3" /> : null;
    const legend = settings.legend ? (
      <Legend
        verticalAlign="bottom"
        height={24}
        formatter={(val) => formatVarLabel(val)}
      />
    ) : null;

    const makeLabelList = (dataKey = 'value') => {
      if (!settings.dataLabels) return null;
      // For bar-like charts place labels nicely: horizontal → right (outside), vertical → top
      const barTypes = ['bar', 'histogram', 'barGroup', 'crosstabGroup', 'awareness', 'transposeGroup'];
      let pos = 'top';
      if (barTypes.includes(chartType)) {
        pos = (orientation === 'horizontal') ? 'right' : 'top';
      }
      return (
        <LabelList
          dataKey={dataKey}
          position={pos}
          formatter={valueFmt}
        />
      );
    };

    /* Bar / Histogram */
    if (chartType === 'bar' || chartType === 'histogram') {
      return (
        <ResponsiveContainer width="100%" height={Math.max(300, processed.length * 28)}>
          <BarChart
            data={processed}
            layout={orientation === 'horizontal' ? 'vertical' : 'horizontal'}
          >
            {grid}
            {orientation === 'horizontal' ? (
              <>
                <YAxis type="category" dataKey="label" width={120} interval={0} tick={{ fontSize: 12 }} />
                <XAxis type="number" domain={[0, domainMax]} tickFormatter={valueFmt} />
              </>
            ) : (
              <>
                <XAxis dataKey="label" interval={0} tick={{ fontSize: 12 }} angle={-45} textAnchor="end" />
                <YAxis domain={[0, domainMax]} tickFormatter={valueFmt} />
              </>
            )}
            <Tooltip formatter={valueFmt} />
            {legend}
            <Bar dataKey="value" fill={primaryColor}>
              {makeLabelList()}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }

    /* Line */
    if (chartType === 'line') {
      return (
        <ResponsiveContainer width="100%" height={Math.max(300, processed.length * 28)}>
          <LineChart data={processed}>
            {grid}
            <XAxis dataKey="label" interval={0} tick={{ fontSize: 12 }} />
            <YAxis domain={[0, domainMax]} tickFormatter={valueFmt} />
            <Tooltip formatter={valueFmt} />
            {legend}
            <Line type="monotone" dataKey="value" stroke={primaryColor} dot>
              {makeLabelList()}
            </Line>
          </LineChart>
        </ResponsiveContainer>
      );
    }

    /* Area */
    if (chartType === 'area') {
      return (
        <ResponsiveContainer width="100%" height={Math.max(300, processed.length * 28)}>
          <AreaChart data={processed}>
            {grid}
            <XAxis dataKey="label" interval={0} tick={{ fontSize: 12 }} />
            <YAxis domain={[0, domainMax]} tickFormatter={valueFmt} />
            <Tooltip formatter={valueFmt} />
            {legend}
            <Area type="monotone" dataKey="value" fill={primaryColor} stroke={primaryColor}>
              {makeLabelList()}
            </Area>
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    /* Pie */
    if (chartType === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Tooltip formatter={valueFmt} />
            {legend}
            <Pie
              data={processed}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              outerRadius={90}
              label={settings.dataLabels ? (item) => `${item.label} (${valueFmt(item.value)})` : false}
            >
              {processed.map((_, i) => (
                <Cell key={i} fill={palette[i % palette.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      );
    }

    /* Scatter */
    if (chartType === 'scatter') {
      const scat = processed.map((d) => ({ x: d.index, y: d.value, label: d.label }));
      return (
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart>
            {grid}
            <XAxis type="number" dataKey="x" name="index" domain={['dataMin', 'dataMax']} tick={{ fontSize: 12 }} />
            <YAxis type="number" dataKey="y" name="value" domain={[0, domainMax]} tickFormatter={valueFmt} />
            <Tooltip formatter={valueFmt} cursor={{ strokeDasharray: '3 3' }} />
            {legend}
            <Scatter data={scat}>{makeLabelList()}</Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      );
    }

    /* Radar */
    if (chartType === 'radar') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={processed}>
            {grid}
            <PolarAngleAxis dataKey="label" />
            <PolarRadiusAxis tickFormatter={valueFmt} domain={[0, domainMax]} />
            <Radar dataKey="value" stroke={primaryColor} fill={primaryColor} fillOpacity={0.6} />
            <Tooltip formatter={valueFmt} />
            {legend}
          </RadarChart>
        </ResponsiveContainer>
      );
    }

    /* barGroup */
    if (chartType === 'barGroup') {
      const serKeys = graph.data.series;                            // ["A","B",…]
      const baseData = hasRowPivot
        ? hierarchicalRows.filter(r=>r.__isTotal && r.__level === 0)
        : filteredRecords;
      const data = baseData;
      const yKey  = hasRowPivot ? rowGroupBy[0] : 'label';
      const colors = palette;
      const fmt = v => metric === 'percentage' ? fmtPercent(v) : v;

      // --- Normalize barGroup values to percentages when needed ---
      let chartData = data;
      if (metric === 'percentage') {
        chartData = data.map(rec => {
          const participantsDen = graph?.bases?.participants ?? 0;
          const baseTotal = (graph.data.series || []).reduce((s,k)=> s + (rec[k] ?? 0), 0) || 1;
          const den = participantsDen > 0 ? participantsDen : baseTotal;
          const out = { ...rec };
          serKeys.forEach(k => {
            if (k === 'Mean') return;                // Mean hep sayı
            out[k] = +(((rec[k] ?? 0) * 100) / den).toFixed(1);
          });
          return out;
        });
      }

      const isHorizontal = orientation === 'horizontal';   // yeni
      const layout       = isHorizontal ? 'vertical' : 'horizontal';  // yeni


      return (
        <Box>
          <ResponsiveContainer width="100%" height={Math.max(300, processed.length * 28)}>
            <BarChart data={chartData} layout={layout}>
              {grid}
              {isHorizontal ? (
                <>
                  <YAxis dataKey={yKey} type="category" width={160} />
                  <XAxis type="number" domain={[0, 'dataMax']} tickFormatter={fmt} />
                </>
              ) : (
                <>
                  <XAxis
                    dataKey={yKey}
                    type="category"
                    interval={0}
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                  />
                  <YAxis type="number" domain={[0, 'dataMax']} tickFormatter={fmt} />
                </>
              )}
              <Tooltip formatter={fmt} />
              {legend}
              {serKeys.map((k, sIdx) => (
                <Bar key={k} dataKey={k} fill={colors[sIdx % colors.length]}>
                  {settings.dataLabels && (
                    <LabelList dataKey={k} position={isHorizontal ? 'right' : 'top'} formatter={fmt} />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        
        </Box>
      );
    }

    /* crosstabGroup */
    if (chartType === 'crosstabGroup') {
      const serKeys = graph.data.series;
      const baseRaw = hasPivot
        ? groupedTotals
        : filteredRecords.map((r) => ({ ...r, answer: r.answer ?? r.label }));
      const rawData = baseRaw;
      const onlyBaseRows = rawData;
      // --- Metric‑aware transform ---
      const chartData = metric === 'percentage'
        ? onlyBaseRows.map(rec => {
            const participantsDen = graph?.bases?.participants ?? 0;
            const baseTotal = (graph.data.series || []).reduce((s,k)=> s + (rec[k] ?? 0), 0) || 1;
            const den = participantsDen > 0 ? participantsDen : baseTotal;
            const out = { ...rec };
            serKeys.forEach(k => {
              if (k === 'Mean') return;
              out[k] = +(((rec[k] ?? 0) * 100) / den).toFixed(1);
            });
            return out;
          })
        : onlyBaseRows;
      const fmt       = v => metric === 'percentage' ? fmtPercent(v) : v;
      const domainMax = metric === 'percentage'
        ? 100
        : Math.max(...chartData.map(rec => serKeys.reduce((s,k)=>s + (rec[k] ?? 0), 0)));
      // chartData already prepared above
      const isHorizontal = orientation === 'horizontal';
      const layout = isHorizontal ? 'vertical' : 'horizontal';
      const keyAxis = hasPivot ? groupBy : 'answer';
      const colors = palette;

      return (
        <Box>
          <ResponsiveContainer width="100%" height={Math.max(300, processed.length * 28)}>
            <BarChart data={chartData} layout={layout}>
              {grid}
              {isHorizontal ? (
                <>
                  <YAxis dataKey={keyAxis} type="category" width={hasPivot ? 140 : 200} />
                  <XAxis type="number" domain={[0, domainMax]} tickFormatter={fmt} />
                </>
              ) : (
                <>
                  <XAxis dataKey={keyAxis} type="category" interval={0} tick={{ fontSize: 12 }} angle={-45} textAnchor="end" />
                  <YAxis type="number" domain={[0, domainMax]} tickFormatter={fmt} />
                </>
              )}
              <Tooltip formatter={fmt} />
              {serKeys.map((k, i) => (
                <Bar
                  key={k}
                  dataKey={k}
                  fill={colors[i % colors.length]}
                  stackId="a"
                >
                  {settings.dataLabels && (
                    <LabelList dataKey={k} position={isHorizontal ? 'right' : 'top'} formatter={fmt} />
                  )}
                </Bar>
              ))}
              {legend}
            </BarChart>
          </ResponsiveContainer>

        </Box>
      );
    }

    /* awareness  (3 ayrı BarChart yan yana) */
    if (chartType === 'awareness') {
      const serKeys = graph.data.series;          // ör. ["M1","M2","M3"]
      const data    = filterByVisibility(graph.data.records, r => r.label);
      const isPercent = metric === 'percentage';
      const domainMax = isPercent ? 100 : 'dataMax';

      /* Ortak eksen biçimi */
      const makeYAxis = (showLabels) => (
        <YAxis
          dataKey="label"
          type="category"
          width={showLabels ? 160 : 0}
          tick={showLabels ? { fontSize: 12 } : false}
          axisLine={false}
          tickLine={false}
          interval={0}
        />
      );

      return (
        <Box sx={{ display:'flex', width:'100%' }}>
          {serKeys.map((k, idx) => (
            <Box key={k} sx={{ flex:1, px:1 }}>
              <Typography variant="subtitle2" align="center" sx={{ mb:0.5 }}>
                {formatVarLabel(k)}
              </Typography>

              <ResponsiveContainer width="100%" height={Math.max(300, processed.length * 28)}>
                <BarChart data={data} layout="vertical">
                  {settings.gridLines && <CartesianGrid strokeDasharray="3 3" />}
                  {idx === 0 ? makeYAxis(true) : makeYAxis(false)}
                  <XAxis
                    type="number"
                    domain={[0, domainMax]}
                    hide
                  />
                  <Tooltip formatter={(v)=> isPercent?`${v}%`:v } />

                  <Bar
                    dataKey={k}
                    fill={palette[idx % palette.length]}
                  >
                    {settings.dataLabels && (
                      <LabelList
                        dataKey={k}
                        position="insideRight"
                        formatter={(v)=> isPercent?`${v}%`:v }
                      />
                    )}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ))}
        </Box>
      );
    }

    /* transposeGroup */
    if (chartType === 'transposeGroup') {
      const data       = buildTransposedData(graph.data);
      const serKeys    = graph.data.records.map((r) => r.label);  // eski label’lar
      // --- Normalize transposeGroup values to percentages when needed ---
      let chartData = data;
      if (metric === 'percentage') {
        const participantsDen = graph?.bases?.participants ?? 0;
        const den = participantsDen > 0 ? participantsDen : 1;
        chartData = data.map(rec => {
          const out = { ...rec };
          serKeys.forEach(k => {
            const raw = rec[k] ?? 0;
            out[k] = +(((raw) * 100) / den).toFixed(1);
          });
          return out;
        });
      }
      
      const colors     = palette;
      const fmt         = v => metric === 'percentage' ? fmtPercent(v) : v;
      const domainMax   = metric === 'percentage' ? 100 : 'dataMax';
      const isHorizontal = orientation === 'horizontal';
      const layout       = isHorizontal ? 'vertical' : 'horizontal';

      return (
        <Box>
          <ResponsiveContainer width="100%" height={Math.max(300, processed.length * 28)}>
            <BarChart data={chartData} layout={layout}>
              {grid}
              {isHorizontal ? (
                <>
                  <YAxis dataKey="label" type="category" width={140}/>
                  <XAxis type="number" domain={[0, domainMax]} tickFormatter={fmt}/>
                </>
              ) : (
                <>
                  <XAxis dataKey="label" type="category" interval={0}
                        tick={{ fontSize:12 }} angle={-45} textAnchor="end"/>
                  <YAxis type="number" domain={[0, domainMax]} tickFormatter={fmt}/>
                </>
              )}
              <Tooltip formatter={fmt}/>
              {legend}
              {serKeys.map((k, i) => (
                <Bar key={k} dataKey={k} fill={colors[i % colors.length]} stackId="a">
                  {settings.dataLabels && (
                    <LabelList dataKey={k} position={isHorizontal ? 'right' : 'top'} formatter={fmt} />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>

         
        </Box>
      );
    }



    return null;
  };

  /* ---------- 1. TÜM KONTROL ELEMANLARINI TEK DEĞİŞKENDE TOPLA ---------- */
  const compactToggleSx = {
    '& .MuiToggleButton-root': {
      px: 0.75,
      py: 0.35,
      fontSize: '0.75rem',
      lineHeight: 1.2,
    },
  };

  const controls = (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        px: { xs: 1.25, md: 1.75 },
        py: 0.75,
        borderRadius: 2,
        borderColor: 'rgba(148, 163, 184, 0.2)',
        backgroundColor: 'rgba(248, 250, 252, 0.85)',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        flexWrap="wrap"
        rowGap={0.75}
        sx={{
          width: '100%',
          maxWidth: '100%',
          justifyContent: 'flex-start',
          columnGap: 0.75,
          '& .MuiFormControl-root': {
            minWidth: 120,
          },
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* Rapora ekle */}
          <MuiTooltip title="Rapora Ekle">
            <IconButton
              size="small"
              onClick={() => onAddToPresentation(graph)}
            >
              <PlaylistAddIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>

          <MuiTooltip title="Cevapları Düzenle">
            <IconButton size="small" onClick={() => setAnswerDlg(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>

          <MuiTooltip title={showTable ? 'Grafik' : 'Tablo'}>
            <IconButton size="small" onClick={() => setShowTable(prev => { const nv = !prev; onSaveUi && onSaveUi({ showTable: nv }); return nv; })}>
              {showTable ? (
                <ShowChartIcon fontSize="small" />
              ) : (
                <TableChartIcon fontSize="small" />
              )}
            </IconButton>
          </MuiTooltip>

          <MuiTooltip title="Dışa Aktar">
            <span>
              <IconButton
                size="small"
                onClick={handleOpenDownloadMenu}
                disabled={isExporting}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </MuiTooltip>
        </Stack>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: 28,
            mx: 0.5,
            borderColor: 'rgba(148, 163, 184, 0.2)',
            display: { xs: 'none', sm: 'block' },
          }}
        />

        {/* --- Grafik tipi --- */}
        {!showTable && (
          <FormControl size="small" variant="outlined">
            <InputLabel>Tip</InputLabel>
            <Select
              label="Tip"
              value={chartType}
              onChange={(e) => { setChartType(e.target.value); onSaveUi && onSaveUi({ chartType: e.target.value }); }}
              sx={{ fontSize: '0.8rem' }}
            >
              {allowedTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* --- Sıralama --- */}
        {!showTable && (
          <ToggleButtonGroup
            orientation="horizontal"
            exclusive
            size="small"
            value={sortOrder}
            onChange={(e, v) => { if (v !== null) { setSortOrder(v); onSaveUi && onSaveUi({ sortOrder: v }); } }}
            sx={compactToggleSx}
          >
            <ToggleButton value="none" title="Sırasız">–</ToggleButton>
            <ToggleButton value="asc" title="Artan">
              <NorthIcon fontSize="inherit" />
            </ToggleButton>
            <ToggleButton value="desc" title="Azalan">
              <SouthIcon fontSize="inherit" />
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {/* --- Yön --- */}
        {!showTable && ['bar', 'histogram', 'crosstabGroup', 'barGroup','transposeGroup'].includes(chartType) && (
          <ToggleButtonGroup
            orientation="horizontal"
            exclusive
            size="small"
            value={orientation}
            onChange={(e, v) => { if (v !== null) { setOrientation(v); onSaveUi && onSaveUi({ orientation: v }); } }}
            sx={compactToggleSx}
          >
            <ToggleButton value="vertical" title="Dikey">
              <SwapVertIcon fontSize="inherit" />
            </ToggleButton>
            <ToggleButton value="horizontal" title="Yatay">
              <SwapHorizIcon fontSize="inherit" />
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {/* --- Renk Teması --- */}
        {!showTable && (
          <FormControl size="small" variant="outlined">
            <InputLabel>Renk Teması</InputLabel>
            <Select
              label="Renk Teması"
              value={colorThemeId}
              onChange={(e) => {
                const next = e.target.value;
                const persistedTheme = persistedUi && persistedUi.colorTheme;
                const fallback = defaultColorThemeId || DEFAULT_THEME_ID;
                if (persistedTheme) {
                  colorThemeOverrideRef.current = true;
                } else {
                  colorThemeOverrideRef.current = next !== fallback;
                }
                setColorThemeId(next);
                onSaveUi && onSaveUi({ colorTheme: next });
              }}
              renderValue={(selected) => {
                const theme = getThemeById(selected, themeList);
                return (
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography variant="body2">{theme.label}</Typography>
                    <Stack direction="row" spacing={0.25}>
                      {(theme.palette || []).slice(0, 4).map((color, idx) => (
                        <Box
                          key={`${theme.id}-${idx}`}
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: 0.5,
                            bgcolor: color,
                            border: '1px solid rgba(15, 23, 42, 0.12)',
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                );
              }}
              sx={{ fontSize: '0.8rem' }}
            >
              {themeList.map((theme) => (
                <MenuItem key={theme.id} value={theme.id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">{theme.label}</Typography>
                    <Stack direction="row" spacing={0.4}>
                      {theme.palette.slice(0, 5).map((color, idx) => (
                        <Box
                          key={`${theme.id}-${idx}`}
                          sx={{
                            width: 14,
                            height: 10,
                            borderRadius: 0.5,
                            bgcolor: color,
                            border: '1px solid rgba(15, 23, 42, 0.12)',
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* --- Metriği --- */}
        <ToggleButtonGroup
          orientation="horizontal"
          exclusive
          size="small"
          value={metric}
          onChange={(e, v) => { if (v) { setMetric(v); onSaveUi && onSaveUi({ metric: v }); } }}
          sx={compactToggleSx}
        >
          <ToggleButton value="count" title="Frekans">
            <LooksOneIcon fontSize="inherit" />
          </ToggleButton>
          <ToggleButton value="percentage" title="Yüzde">
            <PercentIcon fontSize="inherit" />
          </ToggleButton>
        </ToggleButtonGroup>

        {/* --- Baz --- */}
        <ToggleButtonGroup
          orientation="horizontal"
          exclusive
          size="small"
          value={baseType}
          onChange={(e, v) => {
            if (!v) return;
            setBaseType(v);
            const newer = { ...settings, baseType: v };
            setSettings(newer);
            onSaveSettings(graph.header, newer);
          }}
          sx={compactToggleSx}
        >
          <ToggleButton value="none" title="Baz Gösterme">
            <NotInterestedIcon fontSize="inherit" />
          </ToggleButton>
          <ToggleButton value="participants" title="Katılımcı">
            <PeopleIcon fontSize="inherit" />
          </ToggleButton>
          <ToggleButton value="answers" title="Cevap">
            <FormatListNumberedIcon fontSize="inherit" />
          </ToggleButton>
        </ToggleButtonGroup>

        {/* barGroup & crosstab için ek Select’ler */}
        {chartType === 'barGroup' && hasRowPivot && (
          <FormControl size="small" sx={{ minWidth: 160 }} variant="outlined">
            <InputLabel>Satır Grupla</InputLabel>
            <Select
              multiple
              label="Satır Grupla"
              value={rowGroupBy}
              onChange={(e) => { const v = e.target.value; setRowGroupBy(v); onSaveUi && onSaveUi({ rowGroupBy: v }); }}
              renderValue={(v) => v.join(' ⨯ ')}
              sx={{ fontSize: '0.8rem' }}
            >
              {graph.data.rowFields.map((f) => (
                <MenuItem key={f} value={f}>
                  <Checkbox checked={rowGroupBy.indexOf(f) > -1} size="small" />
                  <ListItemText primary={formatVarLabel(f)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {chartType === 'crosstabGroup' && hasPivot && (
          <FormControl size="small" sx={{ minWidth: 120 }} variant="outlined">
            <InputLabel>Grupla</InputLabel>
            <Select
              label="Grupla"
              value={groupBy}
              onChange={(e) => { const v = e.target.value; setGroupBy(v); onSaveUi && onSaveUi({ groupBy: v }); }}
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value="pivot">Pivot</MenuItem>
              <MenuItem value="answer">Cevap</MenuItem>
            </Select>
          </FormControl>
        )}
      </Stack>

      <Menu
        anchorEl={downloadAnchorEl}
        open={Boolean(downloadAnchorEl)}
        onClose={handleCloseDownloadMenu}
        keepMounted
      >
        <MenuItem disabled={isExporting} onClick={() => handleDownloadRequest('jpeg')}>
          <ListItemIcon>
            <ImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="JPEG (beyaz zemin)" />
        </MenuItem>
        <MenuItem disabled={isExporting} onClick={() => handleDownloadRequest('png')}>
          <ListItemIcon>
            <CropOriginalIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="PNG (şeffaf)" />
        </MenuItem>
        <MenuItem disabled={isExporting} onClick={() => handleDownloadRequest('pdf')}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="PDF" />
        </MenuItem>
        <MenuItem disabled={isExporting} onClick={() => handleDownloadRequest('pptx')}>
          <ListItemIcon>
            <SlideshowIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="PowerPoint (düzenlenebilir)" />
        </MenuItem>
      </Menu>
    </Paper>
  );

  const renderBaseInfo = () => {
    if (baseType === 'none' || !graph.bases) return null;
    const n =
      baseType === 'participants'
        ? graph.bases.participants
        : graph.bases.answers;
    const label = baseType === 'participants' ? 'Baz (Genel)' : 'Baz (Yanıt Verenler)';
    return (
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
        {`${label}: n = ${n}`}
      </Typography>
    );
  };

  // --- Likert helper & summary renderer ---
  const isLikertGraph = (g) => (g?.graphType === 'likert' || g?.questionType === 'likert_scale');

  const renderLikertSummary = () => {
    if (!isLikertGraph(graph)) return null;
    const bases = graph?.bases || {};
    const mean  = typeof bases.mean === 'number' ? bases.mean : null;
    const tb    = bases.tbcounts || {};
    const order = ['mean', 'TB', 'T2B', 'T3B', 'MB', 'B3B', 'B2B', 'BB'];
    const labels = {
      mean: 'Mean',
      TB  : 'TB',
      T2B : 'T2B',
      T3B : 'T3B',
      MB  : 'MB',
      B3B : 'B3B',
      B2B : 'B2B',
      BB  : 'BB',
    };
    const values = {
      mean: mean !== null ? Number(mean).toFixed(3) : '-',
      TB  : tb.TB  ?? '-',
      T2B : tb.T2B ?? '-',
      T3B : tb.T3B ?? '-',
      MB  : tb.MB  ?? '-',
      B3B : tb.B3B ?? '-',
      B2B : tb.B2B ?? '-',
      BB  : tb.BB  ?? '-',
    };

    return (
      <Paper variant="outlined" sx={{ mt: 1, p: 1.25 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Likert Özeti</Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(8, minmax(70px, 1fr))',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {order.map((k) => (
            <Box key={k} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {labels[k]}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {values[k]}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    );
  };

  /* ----------------------------------------------------------- */
  return (
    <Box
      ref={cardRef}
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        mt: 0.75,
        pb: { xs: 1.25, md: 1.75 },
      }}
    >
      {controls}

      <Box
        sx={{
          borderRadius: 2,
          border: '1px solid rgba(148, 163, 184, 0.16)',
          backgroundColor: '#F8FAFC',
          boxShadow: '0 12px 20px rgba(148, 163, 184, 0.14)',
          px: { xs: 1.25, md: 1.75 },
          py: { xs: 1.25, md: 1.5 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          overflowX: 'auto',
        }}
      >
        {isVisible ? (
          <>
            <Box
              ref={chartContentRef}
              sx={{ position: 'relative', width: '100%' }}
            >
              {showTable ? (
                <DataGrid
                  {...buildGridMeta()}
                  autoHeight
                  pageSizeOptions={[5, 10, 25, 100]}
                  disableRowSelectionOnClick
                  slots={{ toolbar: GridToolbar }}
                  columnBuffer={6}
                />
              ) : (
                renderChart()
              )}
            </Box>
            {renderBaseInfo()}
            {renderLikertSummary()}
          </>
        ) : (
          <Box
            sx={{
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
      </Box>

      <AnswerSelectDialog open={answerDlg} onClose={() => setAnswerDlg(false)} options={allAnswerOptions} selected={settings.visibleAnswers ?? allAnswerOptions} onSave={handleSaveAnswers} />
    </Box>
  );
}
